import _ from 'lodash';
import moment from 'moment';
import { useEffect, useImperativeHandle, useRef, useState, type ForwardedRef } from 'react';
import {
  fetchHolterEvents,
  applyGroupEventChange,
  formatEventStrip,
  isAppliedFilterEvent,
  updateEventChangeAndNew,
} from '@/entities/event';
import {
  KEY_CANCEL,
  KEY_RECOIL,
  STRIP_EVENT_THUMBNAIL_INFO,
  TYPE_RHYTHM_EVENT_ENUM,
  typesPersistEventV2,
  LIST_RUN_EVENT,
} from '@/shared/config';
import { useUpdateDeepCompareEffect, useUpdateEffect } from '@/shared/lib/customHooks';
import { formatHrData, type ChartHRHandle } from '@/entities/heart-rate';
import { logError } from '@/shared/lib/apiHelpers';
import {
  selectActiveTab,
  selectDailyCount,
  selectEventChanges,
  selectEventFilter,
  selectEventNews,
  selectIsEmptyData,
  selectIsLoadingHrChart,
  selectReloadEcgViewer,
  selectReloadHrChart,
  selectSelectedDateValue,
  selectCombinedEvents,
  selectGroupEventChanges,
  selectUpdatedSelectedStrip,
  useReportStore,
} from '@/entities/report';
import type { HrChartHandle, HrChartProps } from './hrChart';

/**
 * Drives the HR chart of the rhythm-events tab, loading the selected day's holter events and turning the study's hourly ECG summaries into the minutely HR series and event zones the chart draws. Refetches on date, filter and event-change updates, prefetches the neighbouring days after a two-second delay, repaginates the strip list so the updated AFib strip stays on screen, and exposes an arrow-button handler through the forwarded ref.
 *
 * @param props - Study identifiers, timezone offset and the ECG data map for the report.
 * @param ref - Forwarded handle exposing the chart arrow-button action to the parent.
 * @returns An object carrying chartHrRef, keyRecoil, dataChart and updatedSelectedStrip.
 */
const useHrChart = (
  { studyId, profileId, timezoneOffset, ecgDataMap }: HrChartProps,
  ref: ForwardedRef<HrChartHandle>,
) => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;

  /**
   * Reads which event-type button is currently active on the rhythm-events tab. Evaluates the tab-keyed active-tab selector against the current report store state instead of subscribing to it.
   *
   * @returns The active rhythm, beat or HR event type.
   */
  const getActiveButton = () => selectActiveTab(keyRecoil)(useReportStore.getState());
  const selectedDateValue = useReportStore(selectSelectedDateValue(keyRecoil));
  const isLoadingHrChart = useReportStore(selectIsLoadingHrChart(keyRecoil));
  const setIsLoadingHrChartAction = useReportStore((s) => s.actions.setIsLoadingHrChart);
  /**
   * Toggles the loading state of the HR chart for this tab. Forwards the value or updater to the store action together with the tab key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsLoadingHrChart = (v: Parameters<typeof setIsLoadingHrChartAction>[1]) =>
    setIsLoadingHrChartAction(keyRecoil, v);
  const eventChanges = useReportStore(selectEventChanges(keyRecoil));
  const eventNews = useReportStore(selectEventNews(keyRecoil));
  const groupEventChanges = useReportStore(selectGroupEventChanges(keyRecoil));
  const dateList = useReportStore((s) => s.dateList);
  const specialEventTypes = useReportStore((s) => s.specialEventTypes);
  const eventFilter = useReportStore(selectEventFilter(keyRecoil));
  const reloadHrChart = useReportStore(selectReloadHrChart(keyRecoil));
  // updatedSelectedStripState (Recoil, Task 7) is typed IEvent | IBeat | IEcgBookmark, missing isNew/idEvent on some members - atom-type gap.
  const updatedSelectedStrip = useReportStore(selectUpdatedSelectedStrip(keyRecoil)) as any;
  const setCombinedEventsAction = useReportStore((s) => s.actions.setCombinedEvents);
  /**
   * Writes the merged event list backing the HR chart for this tab. Forwards the value or updater to the store action together with the tab key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setCombinedEvents = (v: Parameters<typeof setCombinedEventsAction>[1]) =>
    setCombinedEventsAction(keyRecoil, v);
  /**
   * Reads the merged event list currently backing the HR chart. Evaluates the tab-keyed selector against the current report store state instead of subscribing to it.
   *
   * @returns The combined events held for the tab.
   */
  const getCombinedEvents = () => selectCombinedEvents(keyRecoil)(useReportStore.getState());
  /**
   * Reads the per-day event counts used to offset strip pagination. Evaluates the tab-keyed selector against the current report store state instead of subscribing to it.
   *
   * @returns The daily count entries held for the tab.
   */
  const getDailyCount = () => selectDailyCount(keyRecoil)(useReportStore.getState());
  /**
   * Reads whether the selected day has no HR data to draw. Evaluates the tab-keyed selector against the current report store state instead of subscribing to it.
   *
   * @returns True when the tab currently has no data.
   */
  const getIsEmptyData = () => selectIsEmptyData(keyRecoil)(useReportStore.getState());
  const setIsEmptyDataAction = useReportStore((s) => s.actions.setIsEmptyData);
  /**
   * Marks whether the selected day has any HR data to draw. Forwards the value or updater to the store action together with the tab key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsEmptyData = (v: Parameters<typeof setIsEmptyDataAction>[1]) =>
    setIsEmptyDataAction(keyRecoil, v);
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Moves the strip pagination of this tab to another page. Forwards the value or updater to the store action together with the tab key.
   *
   * @param v - The new page index value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const reloadEcgViewer = useReportStore(selectReloadEcgViewer(keyRecoil));
  const isRenderViewerEcg = useReportStore((s) => s.isRenderViewerEcg);

  const [dataChart, setDataChart] = useState<any>();

  const fetchInBackgroundTimeout = useRef(null);
  const chartHrRef = useRef<ChartHRHandle>(null);
  const lastFilterRef = useRef(eventFilter);

  let isActiveQuery: boolean;

  /**
   * Warms the holter-event cache for a neighbouring day so a date switch renders without a visible fetch. Builds a persist-event filter for the study profile over the given window and issues a cancellable background query, logging any failure.
   *
   * @param startSearchDate - Inclusive start of the day window to prefetch.
   * @param stopSearchDate - Exclusive end of the day window to prefetch.
   * @returns A promise resolving once the background query has been issued.
   */
  const fetchHolterEventsBackgroundV2 = async (startSearchDate: any, stopSearchDate: any) => {
    try {
      const holterEventsPersistFilter = {
        studyId,
        profileId,
        types: typesPersistEventV2,
        startSearchDate,
        stopSearchDate,
      };
      fetchHolterEvents(holterEventsPersistFilter, 0, true, KEY_CANCEL.API_HOLTER_AI);
    } catch (error) {
      logError('Failed to fetch holter events in background: ', error);
    }
  };

  /**
   * Applies the tab's event filter to the holter events fetched for one event type. Returns the list untouched when no filter is active for that type, otherwise drops events lacking captured strips, events already reviewed and unchanged, and - for run events - those whose average HR is missing or at least 100.
   *
   * @param events - The holter events to narrow down.
   * @param type - The event type the filter settings apply to.
   * @returns The filtered event list.
   */
  const filterEvents = (events: any[], type: any) => {
    if (!isAppliedFilterEvent(eventFilter, type)) {
      return events;
    }
    let keepEvent;
    return _.filter(events, (event: any) => {
      keepEvent = true;
      if (event.type === type) {
        if (eventFilter.isCapture && _.isEmpty(event.stripImgs)) {
          keepEvent = false;
        }
        if (eventFilter.isHideReviewed && event.isReviewed && !event.isChanged) {
          keepEvent = false;
        }
        if (eventFilter.isAvgHrBelow100 && LIST_RUN_EVENT.includes(type)) {
          if (!_.isNumber(event.avgHr) || event.avgHr >= 100) {
            keepEvent = false;
          }
        }
        return keepEvent;
      }
      return keepEvent;
    });
  };

  /**
   * Fetches the selected day's persisted holter events and rebuilds the HR chart from them. Shows the loading state, queries the day window, merges local event changes and newly added events, applies the tab filter and group changes, stores the combined list, then hands the chart-relevant subset to the HR formatter - all skipped when the query is no longer the active one.
   *
   * @param needLoading - Whether to show the chart loading state while fetching.
   * @returns A promise resolving once the day's events are stored and the chart data rebuilt.
   */
  const fetchHolterEventsByDateV2 = async (needLoading = true) => {
    if (needLoading) {
      setIsLoadingHrChart(true);
    }
    let downloadedPersistEvents: any[] = [];
    try {
      const start = performance.now();
      const type = getActiveButton();
      const startSearchDate = selectedDateValue;
      const stopSearchDate = moment(selectedDateValue).add(1, 'days')?.toISOString();
      const holterEventsPersistFilter = {
        studyId,
        profileId,
        types: typesPersistEventV2,
        startSearchDate,
        stopSearchDate,
      };
      const result = await fetchHolterEvents(holterEventsPersistFilter, 0, true, null, false);
      if (performance.now() - start < 100) {
        setIsLoadingHrChart(false);
      }
      if (result?.events) {
        downloadedPersistEvents = [...result.events];
      }
      if (isActiveQuery) {
        const mergeEvents = downloadedPersistEvents.sort(
          (a, b) => Date.parse(a.start) - Date.parse(b.start),
        );
        const dataFormat = formatEventStrip(mergeEvents);
        const holterEventsByDateUpdate = updateEventChangeAndNew(
          dataFormat,
          eventChanges,
          eventNews,
          startSearchDate,
          stopSearchDate,
          typesPersistEventV2,
          true,
        );
        const filterHolterEventsByDateUpdate = filterEvents(holterEventsByDateUpdate, type);
        applyGroupEventChange(filterHolterEventsByDateUpdate, groupEventChanges);
        setCombinedEvents(filterHolterEventsByDateUpdate);
        const holterEventsByDate = filterHolterEventsByDateUpdate.filter(
          (x) => typesPersistEventV2.includes(x.type) || typesPersistEventV2.includes(x.oldType),
        );
        formatHrData({
          holterEventsByDate,
          needLoading,
          ecgDataMap,
          timezoneOffset,
          selectedDateValue,
          setDataChart,
          setIsEmptyData,
          setIsLoadingHrChart,
        });
      }
    } catch (error) {
      logError('Failed to fetch holter events: ', error);
    }
  };

  useEffect(() => {
    // *: Change to other date
    isActiveQuery = true;
    if (selectedDateValue && isRenderViewerEcg) {
      chartHrRef.current.resetCurrentSelect();
      fetchHolterEventsByDateV2();
    }
    return () => {
      isActiveQuery = false;
    };
  }, [selectedDateValue, specialEventTypes, eventFilter, reloadHrChart, isRenderViewerEcg]);

  useEffect(() => {
    if (dateList.length) {
      const currentSelectedDateIndex = _.findIndex(
        dateList,
        (x) => x.toISOString() === selectedDateValue,
      );
      if (currentSelectedDateIndex > -1) {
        if (currentSelectedDateIndex === 0) {
          const firstStartSearchDate = moment(dateList[currentSelectedDateIndex + 1]).toISOString();
          const firstStopSearchDate = moment(firstStartSearchDate).add(1, 'days').toISOString();
          fetchInBackgroundTimeout.current = setTimeout(() => {
            fetchHolterEventsBackgroundV2(firstStartSearchDate, firstStopSearchDate);
          }, 2000);
        } else if (currentSelectedDateIndex === dateList.length - 1) {
          const firstStartSearchDate = moment(dateList[currentSelectedDateIndex - 1]).toISOString();
          const firstStopSearchDate = moment(firstStartSearchDate).add(1, 'days').toISOString();
          fetchInBackgroundTimeout.current = setTimeout(() => {
            fetchHolterEventsBackgroundV2(firstStartSearchDate, firstStopSearchDate);
          }, 2000);
        } else {
          const firstStartSearchDate = moment(dateList[currentSelectedDateIndex + 1]).toISOString();
          const firstStopSearchDate = moment(firstStartSearchDate).add(1, 'days').toISOString();
          const secondStartSearchDate = moment(
            dateList[currentSelectedDateIndex - 1],
          ).toISOString();
          const secondStopSearchDate = moment(secondStartSearchDate).add(1, 'days').toISOString();
          fetchInBackgroundTimeout.current = setTimeout(() => {
            fetchHolterEventsBackgroundV2(firstStartSearchDate, firstStopSearchDate);
            fetchInBackgroundTimeout.current = setTimeout(() => {
              fetchHolterEventsBackgroundV2(secondStartSearchDate, secondStopSearchDate);
            }, 2000);
          }, 2000);
        }
      }
    }
    return () => {
      clearTimeout(fetchInBackgroundTimeout.current);
      fetchInBackgroundTimeout.current = null;
    };
  }, [dateList, selectedDateValue]);

  // *: Fetch and re-draw events
  // *: Fetch and re-draw events
  useUpdateDeepCompareEffect(
    () => {
      isActiveQuery = true;
      if (selectedDateValue && isRenderViewerEcg) {
        fetchHolterEventsByDateV2(false);
      }
    },
    [eventChanges, eventNews, groupEventChanges, reloadEcgViewer, isRenderViewerEcg],
    () => {
      isActiveQuery = false;
    },
  );

  useUpdateEffect(() => {
    if (!isLoadingHrChart) {
      const activeButton = getActiveButton();
      const isAppliedFilter = isAppliedFilterEvent(eventFilter, activeButton);
      const lastFilter = _.cloneDeep(lastFilterRef.current);
      const isResetSort =
        lastFilter.sortBy && lastFilter.sortOrder && !eventFilter.sortOrder && !eventFilter.sortBy;
      lastFilterRef.current = eventFilter;
      if (
        activeButton === TYPE_RHYTHM_EVENT_ENUM.AFIB &&
        !isAppliedFilter &&
        updatedSelectedStrip &&
        !updatedSelectedStrip.isNew &&
        !getIsEmptyData() &&
        !isResetSort
      ) {
        const fromStartToCurrentDailyCount = _.reduce(
          getDailyCount(),
          (result, value) =>
            value.date < selectedDateValue
              ? result + (value.count === -1 ? 0 : value.count)
              : result,
          0,
        );
        let haveEventOverTwoDays = false;
        let idxCount = 0;
        let foundStripIndex = -1;
        const combinedEvents = getCombinedEvents();
        _.forEach(combinedEvents, (x) => {
          if (x.oldType === activeButton) {
            if (selectedDateValue && x.start < selectedDateValue) {
              haveEventOverTwoDays = true;
            }
            if (x.idEvent === updatedSelectedStrip?.idEvent) {
              foundStripIndex = idxCount;
            }
            idxCount += 1;
          }
        });
        if (foundStripIndex > -1) {
          const newPageIndex = Math.floor(
            (foundStripIndex + fromStartToCurrentDailyCount + (haveEventOverTwoDays ? -1 : 0)) /
              STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit,
          );
          setPageIndex((prev) => {
            if (newPageIndex !== prev.index) {
              return { index: newPageIndex };
            }
            return prev;
          });
        }
      }
    }
  }, [updatedSelectedStrip, eventFilter]);

  useImperativeHandle(ref, () => ({
    handleClickArrowButton: (isNext: boolean) => {
      if (chartHrRef.current) {
        chartHrRef.current.handleClickArrowButton(isNext);
      }
    },
  }));

  return {
    chartHrRef,
    keyRecoil,
    dataChart,
    updatedSelectedStrip,
  };
};

export default useHrChart;
